<template>
  <nav v-if="totalPages > 1" class="review-pagination" aria-label="Reviews pagination">
    <button
      class="review-pagination__btn review-pagination__btn--prev"
      :disabled="currentPage === 1"
      aria-label="Previous page"
      @click="onPageChange(currentPage - 1)"
    >
      Prev
    </button>

    <template v-for="(page, idx) in visiblePages" :key="idx">
      <span
        v-if="page === '...'"
        class="review-pagination__ellipsis"
      >&hellip;</span>
      <button
        v-else
        class="review-pagination__btn review-pagination__btn--page"
        :class="{ 'review-pagination__btn--active': page === currentPage }"
        :aria-current="page === currentPage ? 'page' : null"
        @click="onPageChange(page)"
      >
        {{ page }}
      </button>
    </template>

    <button
      class="review-pagination__btn review-pagination__btn--next"
      :disabled="currentPage === totalPages"
      aria-label="Next page"
      @click="onPageChange(currentPage + 1)"
    >
      Next
    </button>
  </nav>
</template>

<script setup>
import { computed } from 'vue';

const MAX_VISIBLE = 5;

const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
  },
  totalPages: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['page-change']);

const visiblePages = computed(() => {
  const pages = [];

  if (props.totalPages <= MAX_VISIBLE) {
    for (let i = 1; i <= props.totalPages; i += 1) {
      pages.push(i);
    }
    return pages;
  }

  const start = Math.max(1, props.currentPage - 2);
  const end = Math.min(props.totalPages, start + MAX_VISIBLE - 1);
  const adjustedStart = Math.max(1, end - MAX_VISIBLE + 1);

  if (adjustedStart > 1) {
    pages.push(1);
    if (adjustedStart > 2) {
      pages.push('...');
    }
  }

  for (let i = adjustedStart; i <= end; i += 1) {
    pages.push(i);
  }

  if (end < props.totalPages) {
    if (end < props.totalPages - 1) {
      pages.push('...');
    }
    pages.push(props.totalPages);
  }

  return pages;
});

function onPageChange(page) {
  if (page >= 1 && page <= props.totalPages && page !== props.currentPage) {
    emit('page-change', page);
  }
}
</script>

<style lang="scss" scoped>
.review-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: calc(var(--grid-unit) / 2);
  margin-top: calc(var(--grid-unit) * 2);

  &__btn {
    background: none;
    border: 1px solid var(--gray-200);
    border-radius: 4px;
    padding: 4px 10px;
    cursor: pointer;
    color: var(--gray-500);
    font-size: var(--font-size-14);

    &:hover:not(:disabled):not(&--active) {
      background-color: var(--gray-100);
    }

    &:disabled {
      opacity: 0.4;
      cursor: default;
    }

    &--active {
      background-color: var(--sapphire-700);
      color: #fff;
      border-color: var(--sapphire-700);
    }

    &--prev,
    &--next {
      font-size: var(--font-size-14);
      padding: 4px 10px;
    }
  }

  &__ellipsis {
    padding: 0 4px;
    color: var(--gray-400);
  }
}
</style>
